import { useEffect, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Typography } from '@mui/material';
import { Calendar } from 'primereact/calendar';
import { AutoComplete } from 'primereact/autocomplete';
import { Menu, MenuMenu } from 'semantic-ui-react';
import Button from '@mui/material/Button';
import "primereact/resources/themes/lara-light-indigo/theme.css";

import classes from './classes.module.scss';

import {
  getAccounts
} from 'app/services/accounts';

import {
  generateBillingReport
} from 'app/services/billings';

import {
  formatCurrency
} from 'app/utils/valueFormatters';

import {
  formatOptions,
  subscriptionOptions
} from 'app/utils/options';

const BillingReport = props => {
  const [loaded, setLoaded] = useState(false);
  const [data, setData] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [filteredAccounts, setFilteredAccounts] = useState([]);

  const subscriptions = subscriptionOptions;
  const dt = useRef(null);

  useEffect(async () => {
    if (!loaded) {

      if (startDate && endDate && selectedAccount) {
        await generateBillingReport({ account_id: selectedAccount.value, start_date: startDate, end_date: endDate }).then((result) => {
          setData(result);
        }).catch((resp) => {
          console.log(resp);
        })
      }
      setLoaded(true);
    }
  }, [loaded])

  const getAccountOptions = async (page = 1, perPage = 100, filter = null) => {
    let params = {
      page,
      per_page: perPage
    }

    if (filter) {
      params.filter_model = {
        name: [{
          filterClass: 'text',
          filterType: 'contains',
          value: filter
        }]
      }
    }

    let resp = await getAccounts(params);
    return formatOptions(resp.records);
  }


  const searchAccounts = (e) => {
    const selected = e.query;

    if (!selected) {
      getAccountOptions().then(recs => {
        setFilteredAccounts(recs);
      });
    }

    if (selected) {
      getAccountOptions(1, 100, selected).then(recs => {
        setFilteredAccounts(recs);
      });
    }
  }

  const currencyBodyTemplate = (rowData, column) => {
    if (rowData.category === "Total Amount Due" && column.field !== "total") {
      return;
    }

    if (rowData.category === "Purchase Refunds" && !["total", "number"].includes(column.field)) {
      return;
    }

    if (!rowData.category.includes("Transaction") && !['total', 'dollar_rate', 'dollar_amount'].includes(column.field)) {
      return;
    } else {
      return <label>{formatCurrency(rowData[column.field], rowData.currency)}</label>
    }
  }

  const numberBodyTemplate = (rowData, column) => {
    if (rowData.category === "Total Amount Due" && column.field !== "total") {
      return;
    }

    if (rowData.category === "Purchase Refunds" && !["total", "number"].includes(column.field)) {
      return;
    }

    if (rowData.category === 'Subscription Amount') {
      return <label>{subscriptions.find((i) => i.value === rowData.number)?.label}</label>;
    } else {
      return <label>{rowData[column.field]}</label>
    }
  }

  const percentBodyTemplate = (rowData, column) => {
    if (!rowData.category.includes("Transaction")) {
      return;
    }
    return <label>{rowData[column.field] + "%"}</label>
  }

  const exportCSV = (selectionOnly) => {
    dt.current.exportCSV({ selectionOnly });
  };

  const handleDateChanged = (dateType, dateValue) => {
    if (dateType === 'start_date') {
      setStartDate(dateValue);
    } else {
      setEndDate(dateValue);
    }
    setLoaded(false);
  }

  let sx = { margin: '0 0.5rem' };

  const header = (
    <div>
      <div className={classes.toolbar}>
        <Menu stackable secondary fluid>
          <Menu.Item>
            <Typography variant="h1">
              Billing Report
            </Typography>
          </Menu.Item>

          <MenuMenu position='right'>
            <Menu.Item>
              <AutoComplete
                value={selectedAccount}
                field="label"
                suggestions={filteredAccounts}
                completeMethod={searchAccounts}
                onChange={(e) => {
                  setSelectedAccount(e.value);
                  setLoaded(false);
                }}
                placeholder={'Select Account'}
                dropdown
              />
            </Menu.Item>
            <Menu.Item>
              <Calendar id="start_date" placeholder='Start Date' value={startDate} onChange={(e) => handleDateChanged('start_date', e.value)} showIcon />
            </Menu.Item>
            <Menu.Item>
              <Calendar id="end_date" placeholder='End Date' value={endDate} onChange={(e) => handleDateChanged('end_date', e.value)} showIcon />
            </Menu.Item>
            <Menu.Item>
              <Button sx={sx} variant='contained' onClick={() => exportCSV(false)} data-pr-tooltip="CSV">Export CSV</Button>
            </Menu.Item>
          </MenuMenu>
        </Menu>
      </div>
    </div>
  );

  return <div>
    <DataTable ref={dt} value={data} className={classes.datatable} header={header}>
      <Column field='category' header="Category" />
      <Column field='number' header="Number" body={numberBodyTemplate} />
      <Column field='sale_total' header="Sale Total" body={currencyBodyTemplate} />
      <Column field='dollar_rate' header="Dollar Rate" body={currencyBodyTemplate} />
      <Column field='percentage_rate' header="Percentage Rate" body={percentBodyTemplate} />
      <Column field='dollar_amount' header="Dollar Amount" body={currencyBodyTemplate} />
      <Column field='percentage_amount' header="Percentage Amount" body={currencyBodyTemplate} />
      <Column field='total' header="Total" body={currencyBodyTemplate} />
    </DataTable>
  </div>
}

export default BillingReport;
